import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, CheckCircle, Search, Zap, Rocket, Users,
  Star, Crown, Shield
} from 'lucide-react';

const accompagnements = [
  {
    icon: Search,
    badge: 'Point de départ',
    title: 'Diagnostic de Croissance Digitale',
    description: 'Le point d\'entrée recommandé. On analyse votre situation, vos outils, votre parcours client et vos blocages pour vous donner une vision claire.',
    features: [
      'Analyse complète de votre activité',
      'Identification des blocages',
      'Plan d\'actions prioritaires',
      'Recommandations concrètes',
    ],
    cta: 'Réserver mon diagnostic',
    highlight: true,
    color: 'primary',
  },
  {
    icon: Zap,
    badge: 'À la carte',
    title: 'Mise en place ciblée',
    description: 'Pour ceux qui savent déjà ce qu\'ils veulent. On implémente la solution spécifique dont vous avez besoin.',
    features: [
      'Site web ou landing page',
      'Assistant IA',
      'Automatisation',
      'Réseaux sociaux',
      'Campagnes publicitaires',
      'Suivi commercial / CRM',
    ],
    cta: 'Discuter de mon besoin',
    highlight: false,
    color: 'accent',
  },
  {
    icon: Users,
    badge: 'Autonomie',
    title: 'Accompagnement Autonome',
    description: 'Installation complète + formation. On met tout en place et on vous forme pour que vous puissiez gérer en toute autonomie.',
    features: [
      'Installation complète',
      'Formation personnalisée',
      'Documentation fournie',
      'Support pendant la transition',
    ],
    cta: 'En savoir plus',
    highlight: false,
    color: 'primary',
  },
  {
    icon: Crown,
    badge: 'Premium',
    title: 'Format Partenaire de Croissance',
    description: 'Pulsia devient votre partenaire de croissance. Gestion globale, optimisation continue, résultats maximisés.',
    features: [
      'Stratégie d\'acquisition globale',
      'Gestion complète',
      'Optimisation continue',
      'Rapports réguliers',
    ],
    cta: 'Découvrir l\'offre',
    highlight: false,
    color: 'accent',
  },
];

const AccompagnementCard = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`relative p-8 rounded-2xl card-gradient border ${
        item.highlight 
          ? 'border-primary/50 shadow-glow' 
          : 'border-border/50'
      } hover:border-primary/30 transition-all`}
    >
      {/* Highlight Badge */}
      {item.highlight && (
        <div className="absolute -top-3 left-8 px-4 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
          Recommandé
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
          item.color === 'primary' ? 'bg-primary/10' : 'bg-accent/10'
        }`}>
          <item.icon className={`w-7 h-7 ${
            item.color === 'primary' ? 'text-primary' : 'text-accent'
          }`} />
        </div>
        <div>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {item.badge}
          </span>
          <h3 className="font-display text-xl font-semibold text-foreground mt-1">
            {item.title}
          </h3>
        </div>
      </div>

      {/* Description */}
      <p className="text-muted-foreground mb-6">
        {item.description}
      </p>

      {/* Features */}
      <ul className="space-y-3 mb-8">
        {item.features.map((feature) => (
          <li key={feature} className="flex items-center gap-3">
            <CheckCircle className={`w-5 h-5 shrink-0 ${
              item.color === 'primary' ? 'text-primary' : 'text-accent'
            }`} />
            <span className="text-sm text-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Link to="/contact" className="block">
        <Button 
          variant={item.highlight ? 'hero' : 'outline'} 
          size="lg" 
          className={`w-full group ${item.highlight ? 'btn-glow' : ''}`}
        >
          {item.cta}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Button>
      </Link>
    </motion.div>
  );
};

export default function AccompagnementsPage() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="hero-glow" />
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />

        <div className="section-container relative z-10">
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="section-label mb-4 inline-block">
              Accompagnements
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Des formats adaptés à{' '}
              <span className="gradient-text">votre maturité</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Que vous ayez besoin d'y voir clair, d'une solution précise ou d'un accompagnement 
              complet, nous avons le format qui vous correspond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Message Section */}
      <section className="py-12">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-2xl bg-card/50 border border-border/50 text-center max-w-3xl mx-auto"
          >
            <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              Vous n'avez pas à choisir seul
            </h2>
            <p className="text-muted-foreground">
              Chaque projet commence par un échange. On comprend votre situation, vos objectifs 
              et vos contraintes, puis on vous recommande le format le plus adapté. Pas de vente 
              forcée, juste du bon sens.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accompagnements Grid */}
      <section className="py-20">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-8">
            {accompagnements.map((item, index) => (
              <AccompagnementCard key={item.title} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="section-label mb-4 inline-block">
              Comment ça marche
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Un parcours{' '}
              <span className="gradient-text">simple</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { num: '01', title: 'Échange', desc: 'On discute de votre situation et vos objectifs.' },
              { num: '02', title: 'Recommandation', desc: 'On vous propose le format le plus adapté.' },
              { num: '03', title: 'Action', desc: 'On passe à la mise en œuvre ensemble.' },
            ].map((step, index) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="number-badge mx-auto mb-4">
                  {step.num}
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-secondary/20 border border-border/50 p-8 lg:p-16 text-center"
          >
            <div className="hero-glow" />
            <div className="relative z-10">
              <Star className="w-12 h-12 text-primary mx-auto mb-6" />
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Prêt à passer à l'action ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Commençons par un diagnostic gratuit pour définir ensemble la meilleure approche pour votre activité.
              </p>
              <Link to="/contact">
                <Button variant="hero" size="xl" className="btn-glow group">
                  Réserver mon diagnostic
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
